import React, { useEffect, useReducer, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import api from '../../../api';
import { Row, Col, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ShopHOC from '../../../hoc/ShopHOC';
import OrderListItem from './OrderListItem/OrderListItem';
import LoadingOverlay from '../../common/LoadingOverlay';
import Pagination from '../../common/Pagination';
import handleClientSideSorting from '../../../utils/handleClientSideSorting';
import { toastrAdd, setOrderRowsMeta } from '../../../actions';

function mapAndSetOrders(orders) {
    return orders.map((order) => {
        let mappedOrder = { ...order, orderRowsTotal: 0 };

        if (order.orderRows.length > 0) {
            order.orderRows.forEach((row) => {
                if (row.status === 3 || row.status === 4) {
                    return {
                        ...mappedOrder,
                        orderRowsTotal: mappedOrder.orderRowsTotal++
                    }
                }
            });
        }

        return mappedOrder;
    });
}

const initialState = {
    orders: [],
    ordersMeta: {},
    loading: true,
    pageCount: 0,
    params: {
        page: 1,
        limit: 10,
        sorter: { column: 'timeCreated', direction: 'desc' },
        clientSideSort: false,
        search: null,
        searchColumns: [
            { column: 'name' },
            { column: 'firstNames' },
            { column: 'lastName' },
            { column: 'email' },
            { column: 'streetAddress' },
            { column: 'invoiceCompany' },
            { column: 'invoiceStreetAddress' },
            { column: 'invoiceCity' }
        ]
    }
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'LOADING':
            return {
                ...state,
                loading: action.loading
            };
        case 'SET_ORDERS':
            return {
                ...state,
                orders: action.orders,
                meta: action.meta,
                pageCount: action.pageCount
            };
        case 'SET_ORDERS_META':
            return {
                ...state,
                ordersMeta: action.ordersMeta,
                loading: false
            }
        case 'SET_PARAMS':
            return {
                ...state,
                params: action.params
            };
        default:
            return state;
    };
};

function OrdersList(props) {
    const { t } = useTranslation();
    const { eventID, orderRowsMeta, limit, asOrderSelect, selectedOrder, onSelectOrder } = props;
    const partnerID = props.partnerID ? props.partnerID : localStorage.getItem('partnerID');
    const [state, stateDispatch] = useReducer(reducer, {
        ...initialState,
        params: {
            ...initialState.params,
            limit,
            filters: {
                'filter[0][column]': 'status',
                'filter[0][condition]': 'in',
                'filter[0][values][0]': '12',
                'filter[1][column]': 'partnerID',
                'filter[1][condition]': 'in',
                'filter[1][values][0]': partnerID
            },
        }
    });
    const { loading, orders, ordersMeta, params, pageCount } = state;
    const sortingOptions = [
        { value: '{ "column": "timeCreated", "direction": "desc" }', text: `${t('general.newest_first')}` },
        { value: '{ "column": "timeCreated", "direction": "asc" }', text: `${t('general.oldest_first')}` },
        { value: '{ "column": "orderRowsTotal", "direction": "desc", "clientSideSort": true, "type": "numeric" }', text: `${t('orders.order_rows')} ↓` },
        { value: '{ "column": "orderRowsTotal", "direction": "asc", "clientSideSort": true, "type": "numeric" }', text: `${t('orders.order_rows')} ↑` },
    ];
    const reduxDispatch = useDispatch();

    const fetchOrders = useCallback((params) => {
        stateDispatch({ type: 'LOADING', loading: true });

        api.shop.getOrders(eventID, {
            ...params,
            ...(params.clientSideSort ? { page: 1, limit: 100 } : {})
        })
            .then((response) => {
                const { orders } = response.data.data;
                const { meta } = response.data;

                stateDispatch({
                    type: 'SET_ORDERS',
                    orders: params.clientSideSort
                        ? handleClientSideSorting(mapAndSetOrders(orders), params.sorter)
                        : mapAndSetOrders(orders),
                    pageCount: Math.ceil(meta.rowsFiltered / meta.limit)
                });
                // use redux state to store total orders for dashboard overview;
                reduxDispatch({ type: 'SET_ORDERS_TOTAL', total: meta.rowsFiltered });
            })
            .finally(() => {
                api.shop.getOrdersMeta()
                    .then((response) => {
                        stateDispatch({ type: 'SET_ORDERS_META', ordersMeta: response.data.data.meta })
                    });
            })
            .catch((error) => {
                stateDispatch({ type: 'LOADING', loading: false });
                reduxDispatch(toastrAdd({
                    code: 0,
                    type: 'error',
                    title: t('errors.orders_error'),
                    details: `${t('general.details')}: ${error.firstError.title} - ${error.firstError.details}`
                }));
            });

        if (!eventID) {
            api.shop.getOrderRowsMeta()
                .then(response => {
                    reduxDispatch(setOrderRowsMeta(response.data.data.meta));
                });
        }
    }, [eventID, reduxDispatch, t]);

    useEffect(() => {
        fetchOrders(params);
    }, [fetchOrders, params]);

    const handlePageSize = ({ target }) => {
        stateDispatch({
            type: 'SET_PARAMS', params: {
                ...params,
                page: 1,
                limit: target.value
            }
        });
    };

    const handleSearch = ({ target }) => {
        stateDispatch({
            type: 'SET_PARAMS', params: {
                ...params,
                page: 1,
                search: target.value.split(' ') || null
            }
        });
    };

    const handleSorting = ({ target }) => {
        const sorter = JSON.parse(target.value);
        stateDispatch({
            type: 'SET_PARAMS',
            params: {
                ...params,
                sorter,
                clientSideSort: sorter.clientSideSort || false
            }
        });
    };

    const paginate = ({ selected }) => {
        stateDispatch({
            type: 'SET_PARAMS', params: {
                ...params,
                page: selected + 1
            }
        });
    };

    const marginClass = asOrderSelect ? 'm-0' : 'mt-0';

    return (
        <Row>
            <Col md={12}>
                {/* !asOrderSelect ?
                    <h3>{t('orders.header')}</h3>
                : null */}
                <Card className={marginClass}>
                    {!asOrderSelect ?
                        <Card.Header>
                            {t('orders.subtitle')}
                        </Card.Header>
                    : null}
                    <div className="container DataListInputs">
                        <div className="row justify-content align-items-center">
                            <div className="col col-sm-auto">
                                <select
                                    className="form-control form-control-sm"
                                    defaultValue={params.limit}
                                    onChange={handlePageSize}
                                    disabled={params.clientSideSort}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                            <div className="col col-sm-auto">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={t('general.search')}
                                    onChange={handleSearch}
                                />
                            </div>
                            <div className="col col-sm-auto">
                                <select
                                    className="form-control form-control-sm"
                                    defaultValue={{ column: 'timeModified', direction: 'desc' }}
                                    onChange={handleSorting}
                                >
                                    {sortingOptions.map((o, i) => (
                                        <option
                                            key={i}
                                            value={o.value}
                                        >
                                            {o.text}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="DataList">
                        {orders.map(order => (
                            <OrderListItem
                                key={order.orderID}
                                order={order}
                                orderMeta={ordersMeta}
                                orderRowsMeta={orderRowsMeta}
                                // if used in order select
                                asOrderSelect={asOrderSelect}
                                selectedOrder={selectedOrder}
                                onSelectOrder={onSelectOrder}
                            />
                        ))}
                        {loading ?
                            <LoadingOverlay />
                        : null}
                    </div>

                    {orders.length ?
                        <Card.Footer>
                            <div className="pull-right">
                                <Pagination
                                    pageCount={pageCount}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={5}
                                    onPageChange={paginate}
                                    forcePage={params.page - 1}
                                />
                            </div>
                        </Card.Footer>
                    : null}

                    {!orders.length ?
                        <Card.Body>
                            {t('orders.no_orders')}
                        </Card.Body>
                    : null}
                </Card>
            </Col>
        </Row>
    );
}

export default ShopHOC(OrdersList);